/*
 * Copyright (c) 2021-2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 *
 *
 */

import React, {useRef, useState} from 'react';
import PropTypes from "prop-types";
import GlobalPortal from "./GlobalPortal.jsx";
import {
    arrow,
    autoPlacement,
    FloatingArrow,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import DisplayIcon from "./Icons/DisplayIcon.jsx";
import {offset} from "@floating-ui/react-dom";


function InfoTooltip(props) {
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef(null);

    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset({mainAxis: props.offset ?? 6}),
            autoPlacement({}),
            arrow({
                element: arrowRef,
            }),
        ],
    });

    const hover = useHover(context, {
        move: false,
        delay: {open: props.delay, close: 0},
        enabled: true
    });

    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, {role: 'tooltip'});

    const {getReferenceProps, getFloatingProps} = useInteractions([
        hover,
        focus,
        dismiss,
        role,
    ]);

    const showTooltip = isOpen || props.showTooltip;

    return (
        <>
            <div
                className={props.className}
                ref={refs.setReference}
                {...getReferenceProps({
                    onClick: props.onClick,
                    "aria-label": Array.isArray(props.text) ? props.text.toString() : props.text ?? ""
                })}
            >
                {props.children}
            </div>

            {showTooltip && (
                <GlobalPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="absolute z-100"
                    >
                        <FloatingArrow
                            ref={arrowRef}
                            context={context}
                            tipRadius={1}
                            width={15}
                            height={4}
                            className="fill-blue-600"
                        />

                        <div
                            className="rounded-lg shadow-lg p-3 bg-blue-600 text-white text-sm flex items-center gap-2 pointer-events-none opacity-90 transition-all">
                            <DisplayIcon iconName="PiInfo" className="size-5!"/>
                            <span>
                                {Array.isArray(props.text) ? props.text.toString() : props.text ?? ""}
                            </span>
                        </div>
                    </div>
                </GlobalPortal>
            )}
        </>
    );
}


InfoTooltip.propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
    isActive:
    PropTypes.bool,
    className:
    PropTypes.string,
    delay:
    PropTypes.number,
};
InfoTooltip.defaultProps = {
    delay: 0,
    isActive:
        true
}

export default InfoTooltip;
